/**
 * Per-port equipment FOUP transfer state machine + timeline.
 *
 * Synced to the same OHT dwell progress as OhtVehicle3DModel:
 *   DROP  release @ 0.35 · PICKUP take @ 0.75
 *
 * The OHT remains at its designated rail node. DROP uses
 * node → selected load port → chamber; PICKUP reverses that exact path.
 */

import {
  EQUIPMENT_DOCK_HANG_RATIO,
  EQUIPMENT_FOUP_SEAT_BACK_INSET,
} from '../../../../utils/stockerDocks';
import { EQUIPMENT_KIT_WIDTH } from '../../../../utils/layout3d';
import { KIT_FOUP_BASE_WIDTH, resolveKitFoupScale, type EquipmentStatus } from '../types';

export type ShutterState = 'closed' | 'opening' | 'open' | 'closing';

export type PortTransferState =
  | 'idle'
  | 'reserved'
  | 'opening_shutter'
  | 'shutter_open'
  | 'loading'
  | 'unloading'
  | 'closing_shutter'
  | 'error';

export type EquipmentPortOccupancy = 'empty' | 'occupied' | 'loading' | 'unloading' | 'reserved' | 'error';

export type FoupVisualMode = 'hidden' | 'resting' | 'flying';

/** Dwell-normalized fractions for equipment shutter + FOUP motion. */
export const EQUIPMENT_TRANSFER_FRACS = {
  /** OHT stopped / aligned — shutter still closed. */
  alignEnd: 0.1,
  /** DROP: OHT releases FOUP (must match OhtVehicle3DModel). */
  dropOhtRelease: 0.35,
  /** DROP: shutter is fully open after the FOUP has been placed at the node. */
  dropShutterOpenEnd: 0.5,
  /** DROP: FOUP finished entering equipment. */
  dropFoupInside: 0.75,
  /** DROP: shutter closed again. */
  dropShutterClosed: 0.95,
  /** PICKUP: shutter fully open before the FOUP leaves its load port. */
  pickupShutterOpenEnd: 0.28,
  /** PICKUP: FOUP reaches the designated node. */
  pickupFoupAtNode: 0.7,
  /** PICKUP: OHT takes FOUP (must match OhtVehicle3DModel). */
  pickupOhtTake: 0.75,
  /** PICKUP: shutter closed again. */
  pickupShutterClosed: 0.95,
} as const;

export type Vec3 = [number, number, number];

export type EquipmentPortPose = {
  /** Group origin of the load-port bay in equipment-local space. */
  bay: Vec3;
  /** OHT handoff — just outside / above the tray (P0). */
  ohtHandoff: Vec3;
  /** Tray seat — outside shutter (P1). */
  loadPort: Vec3;
  /** Internal dock behind shutter (P2). */
  internal: Vec3;
  /** Shutter panel center when closed. */
  shutter: Vec3;
  shutterSize: Vec3;
  /** Distance the shutter slides up when fully open. */
  shutterOpenTravel: number;
};

/** Load-port deck height in the procedural equipment kit. */
export const EQUIPMENT_DECK_HEIGHT_RATIO = 0.22;

/**
 * A port is a standard AMHS interface, not a scaled decoration of its host
 * tool.  Keeping this opening width and centre-to-centre pitch constant makes
 * a row of load ports read consistently across differently sized equipment.
 */
export const PROCESS_LOAD_PORT_WIDTH = 12;
export const PROCESS_LOAD_PORT_PITCH = 15;
/**
 * Dock depth, fixed for the same reason the width and pitch are. It used to
 * scale with the tool, which gave a 40-unit-long port on a deep tool and a
 * 6-unit one on a shallow tool — for the same 2-unit carrier either way.
 */
export const PROCESS_LOAD_PORT_DEPTH = 12;
/** Cabinet face used by the docks — the rest is frame so they never hang off. */
export const PROCESS_PORT_INNER_RATIO = 0.84;

/** Dimensions of one load-port module after fitting it to its host tool. */
export type ProcessLoadPortMetrics = {
  scale: number;
  width: number;
  pitch: number;
  depth: number;
};

/**
 * Scale load-port hardware with the host equipment while preserving enough
 * room for the standard FOUP. Overlay/DXF tools are often much shallower than
 * the 80×48 reference kit; fixed 12-unit ports made their trays project far
 * outside the tool and overlap neighbouring equipment.
 */
export function resolveProcessLoadPortMetrics(
  width: number,
  height: number,
  depth: number,
  loadPortCount: number
): ProcessLoadPortMetrics {
  const n = clampProcessLoadPortCount(loadPortCount);
  const availablePerPort = Math.max(0, width * PROCESS_PORT_INNER_RATIO) / n;
  const referenceScale = Math.min(
    availablePerPort / PROCESS_LOAD_PORT_WIDTH,
    height / 24,
    depth / 48
  );
  // The lower bound still clears the fixed-size OHT FOUP. The upper bound
  // prevents a large floor-plan polygon from producing industrial-size trays.
  const scale = Math.min(1.25, Math.max(0.45, referenceScale));
  return {
    scale,
    width: Math.min(PROCESS_LOAD_PORT_WIDTH * scale, availablePerPort),
    pitch: PROCESS_LOAD_PORT_PITCH * scale,
    depth: Math.min(PROCESS_LOAD_PORT_DEPTH * scale, depth * 0.56),
  };
}

export function clampProcessLoadPortCount(loadPortCount: number): number {
  return Math.max(1, Math.min(6, Math.round(loadPortCount)));
}

/**
 * Seat X for one port, equipment-local.
 *
 * The streamed drop node sits at the 2D port's along-face fraction, so that
 * fraction — not the bank pitch — decides where the seat goes; the pitch is
 * only the fallback for kits drawn without explicit ports. The result is
 * clamped so a port declared hard against the footprint edge still keeps its
 * whole nest on the cabinet face instead of overhanging the neighbour.
 */
export function resolveLoadPortSeatX(
  fraction: number | undefined,
  width: number,
  nestWidth: number,
  fallback: number
): number {
  const raw =
    typeof fraction === 'number' && Number.isFinite(fraction)
      ? (fraction - 0.5) * width
      : fallback;
  const limit = Math.max(0, (width - nestWidth) * 0.5);
  return Math.min(limit, Math.max(-limit, raw));
}

export type ProcessLoadPortBankFit = {
  pitch: number;
  nestWidth: number;
  bankWidth: number;
};

/**
 * Pack N docks so the outermost nest edges stay inside the cabinet face.
 * Standard pitch when it fits; otherwise the bank is scaled down as a unit.
 */
export function fitProcessLoadPortBank(
  loadPortCount: number,
  cabinetWidth: number = EQUIPMENT_KIT_WIDTH,
  nominalWidth: number = PROCESS_LOAD_PORT_WIDTH,
  nominalPitch: number = PROCESS_LOAD_PORT_PITCH
): ProcessLoadPortBankFit {
  const n = clampProcessLoadPortCount(loadPortCount);
  const inner = Math.max(0.01, cabinetWidth * PROCESS_PORT_INNER_RATIO);
  const nest = Math.min(nominalWidth * 0.94, inner / n);
  if (n <= 1) return { pitch: nominalPitch, nestWidth: nest, bankWidth: nest };
  const natural = (n - 1) * nominalPitch + nest;
  if (natural <= inner) {
    return { pitch: nominalPitch, nestWidth: nest, bankWidth: natural };
  }
  const pitch = inner / n;
  const nestWidth = pitch * 0.88;
  return { pitch, nestWidth, bankWidth: (n - 1) * pitch + nestWidth };
}

export function resolveProcessLoadPortBankWidth(
  loadPortCount: number,
  pitch: number = PROCESS_LOAD_PORT_PITCH
): number {
  const n = clampProcessLoadPortCount(loadPortCount);
  if (n <= 1) return PROCESS_LOAD_PORT_WIDTH;
  return (n - 1) * pitch + PROCESS_LOAD_PORT_WIDTH;
}

export function resolveProcessLoadPortPitch(
  loadPortCount: number,
  width: number = EQUIPMENT_KIT_WIDTH
): number {
  return fitProcessLoadPortBank(loadPortCount, width).pitch;
}

/**
 * 3D cabinet width follows the drawn / layout footprint when provided.
 * Falls back to EQUIPMENT_KIT_WIDTH only when no layout width is available
 * (e.g. unit tests or callers that omit footprint).
 */
export function resolveProcessEquipmentKitWidth(
  _loadPortCount?: number,
  layoutWidth?: number
): number {
  if (typeof layoutWidth === 'number' && Number.isFinite(layoutWidth) && layoutWidth > 0) {
    return layoutWidth;
  }
  return EQUIPMENT_KIT_WIDTH;
}

/**
 * Height the OHT lowers a FOUP to over a tool. It is the deck itself: the hoist
 * seats the FOUP on the tray rather than releasing it in mid-air above it.
 */
export const EQUIPMENT_OHT_HANDOFF_HEIGHT_RATIO = EQUIPMENT_DECK_HEIGHT_RATIO;

export type EquipmentPortVisual = {
  portIndex: number;
  shutterOpen: number;
  shutterState: ShutterState;
  transferState: PortTransferState;
  occupancy: EquipmentPortOccupancy;
  foupMode: FoupVisualMode;
  /** When flying, local position of the single FOUP entity. */
  foupPosition: Vec3 | null;
  isLocked: boolean;
  foupId?: string;
  /** Shutter face LED: red 10s after close, then green / gray. */
  ledStatus?: EquipmentStatus;
};

function clamp01(t: number): number {
  return Math.min(1, Math.max(0, t));
}

export function ease(t: number): number {
  const u = clamp01(t);
  return u * u * (3 - 2 * u);
}

export function lerp3(a: Vec3, b: Vec3, t: number): Vec3 {
  const e = ease(t);
  return [a[0] + (b[0] - a[0]) * e, a[1] + (b[1] - a[1]) * e, a[2] + (b[2] - a[2]) * e];
}

/** Piecewise path P0→P1→P2 (or reverse). `u` in 0..1. */
export function sampleFoupPath(p0: Vec3, p1: Vec3, p2: Vec3, uRaw: number, reverse: boolean): Vec3 {
  const u = clamp01(uRaw);
  if (!reverse) {
    if (u < 0.45) return lerp3(p0, p1, u / 0.45);
    return lerp3(p1, p2, (u - 0.45) / 0.55);
  }
  if (u < 0.55) return lerp3(p2, p1, u / 0.55);
  return lerp3(p1, p0, (u - 0.55) / 0.45);
}

export type EquipmentTransfer2DVisual = {
  shutterOpen: number;
  /** Element-local carrier center. Null means the OHT currently owns it. */
  foupPosition: { x: number; y: number } | null;
};

/**
 * Top-view companion to the 3D path. It uses the same timeline fractions but
 * interpolates directly between the real rail node and selected port anchor.
 */
export function resolveEquipmentTransfer2DVisual(args: {
  isPickup: boolean;
  progress: number;
  node: { x: number; y: number };
  port: { x: number; y: number };
}): EquipmentTransfer2DVisual {
  const p = clamp01(args.progress);
  const F = EQUIPMENT_TRANSFER_FRACS;
  const move = (
    from: { x: number; y: number },
    to: { x: number; y: number },
    u: number
  ) => {
    const e = ease(u);
    return {
      x: from.x + (to.x - from.x) * e,
      y: from.y + (to.y - from.y) * e,
    };
  };

  if (args.isPickup) {
    if (p < F.alignEnd) return { shutterOpen: 0, foupPosition: args.port };
    if (p < F.pickupShutterOpenEnd) {
      return {
        shutterOpen: segmentProgress(p, F.alignEnd, F.pickupShutterOpenEnd),
        foupPosition: args.port,
      };
    }
    if (p < F.pickupFoupAtNode) {
      return {
        shutterOpen: 1,
        foupPosition: move(
          args.port,
          args.node,
          segmentProgress(p, F.pickupShutterOpenEnd, F.pickupFoupAtNode)
        ),
      };
    }
    if (p < F.pickupOhtTake) return { shutterOpen: 1, foupPosition: args.node };
    return {
      shutterOpen:
        p < F.pickupShutterClosed
          ? 1 - segmentProgress(p, F.pickupOhtTake, F.pickupShutterClosed)
          : 0,
      foupPosition: null,
    };
  }

  if (p < F.dropOhtRelease) return { shutterOpen: 0, foupPosition: null };
  if (p < F.dropShutterOpenEnd) {
    return {
      shutterOpen: segmentProgress(p, F.dropOhtRelease, F.dropShutterOpenEnd),
      foupPosition: args.node,
    };
  }
  if (p < F.dropFoupInside) {
    return {
      shutterOpen: 1,
      foupPosition: move(
        args.node,
        args.port,
        segmentProgress(p, F.dropShutterOpenEnd, F.dropFoupInside)
      ),
    };
  }
  return {
    shutterOpen:
      p < F.dropShutterClosed
        ? 1 - segmentProgress(p, F.dropFoupInside, F.dropShutterClosed)
        : 0,
    foupPosition: args.port,
  };
}

/**
 * The one flat load-port deck every port on a tool shares.
 *
 * The seat plane is the OHT handoff plane ({@link EQUIPMENT_DOCK_HANG_RATIO},
 * the same line the rail is snapped onto in 2D). The nest is centred on that
 * plane so the hoist releases onto the pad, and the deck runs the whole bank
 * width so neighbouring ports read as one continuous surface.
 */
export type EquipmentLoadPortDeck = {
  /** Deck top / FOUP base height. */
  deckY: number;
  /** Z of the OHT handoff line / front of the nest (rail side). */
  seatZ: number;
  /** Z where a resting FOUP sits — slightly inboard of {@link seatZ}. */
  foupSeatZ: number;
  /** Deck extents along Z, equipment-local. */
  backZ: number;
  frontZ: number;
  /** Per-port seat footprint on the shared deck. */
  nestWidth: number;
  nestDepth: number;
  /**
   * Z the seat pad is drawn at. Same plane as {@link seatZ} so the hoist
   * releases onto the pad, not past its front lip.
   */
  nestCenterZ: number;
  /**
   * Visible apron for one port. It spans {@link backZ} to {@link frontZ} so the
   * pad meets the cabinet face instead of hovering in front of it, while still
   * ending at the seat's front lip.
   */
  trayDepth: number;
  trayCenterZ: number;
  /** Load-port bay depth used by the tower/chamber meshes. */
  portDepth: number;
  /** Bay origin plane (doorway group Z). */
  bayZ: number;
};

/**
 * Tightest spacing between neighbouring seats, as a fraction of the face.
 *
 * `fitProcessLoadPortBank` packs the bank from `width / count`, which assumes
 * the docks are evenly spread. Layouts carrying the legacy default pair
 * (rx 0.45 / 0.55) sit far closer than that, so the nests would overlap into
 * one blob and neither tray would be centred on its own node.
 */
function minAdjacentFractionGap(
  portFractions: readonly number[] | undefined
): number | undefined {
  const usable = (portFractions ?? []).filter((f) => Number.isFinite(f));
  if (usable.length < 2) return undefined;
  const sorted = [...usable].sort((a, b) => a - b);
  let gap = Infinity;
  for (let i = 1; i < sorted.length; i += 1) {
    gap = Math.min(gap, sorted[i]! - sorted[i - 1]!);
  }
  return Number.isFinite(gap) && gap > 0 ? gap : undefined;
}

export function getProcessEquipmentDeck(
  width: number,
  height: number,
  depth: number,
  loadPortCount: number,
  portFractions?: readonly number[]
): EquipmentLoadPortDeck {
  const metrics = resolveProcessLoadPortMetrics(width, height, depth, loadPortCount);
  const portDepth = metrics.depth;
  const foupSize = KIT_FOUP_BASE_WIDTH * resolveKitFoupScale(width, height, depth);
  const bayZ = depth * 0.3;
  const seatZ = depth * EQUIPMENT_DOCK_HANG_RATIO;
  // Starts inside the cabinet front face so the dock, the doorway, and the
  // shutter meet as one assembly rather than a tray parked in front of one.
  const backZ = depth * 0.2;
  const bank = fitProcessLoadPortBank(
    loadPortCount,
    width,
    metrics.width,
    metrics.pitch
  );
  // Never wider than the room between neighbouring docks, so each tray stays
  // centred on its own node instead of merging with the one beside it.
  const gap = minAdjacentFractionGap(portFractions);
  const nestWidth =
    gap === undefined ? bank.nestWidth : Math.min(bank.nestWidth, gap * width * 0.94);
  // Size the pad from the room behind the hoist, then centre it on the seat
  // so the FOUP lands on the nest instead of hanging off its front edge.
  const nestDepth = Math.min(
    Math.max(0, seatZ - backZ),
    Math.max(foupSize * 1.12, metrics.depth * 0.7)
  );
  const nestCenterZ = seatZ;
  const frontZ = seatZ + nestDepth * 0.5;
  const foupSeatZ = Math.max(
    backZ + nestDepth * 0.28,
    seatZ - nestDepth * EQUIPMENT_FOUP_SEAT_BACK_INSET
  );
  return {
    deckY: height * EQUIPMENT_DECK_HEIGHT_RATIO,
    seatZ,
    foupSeatZ,
    backZ,
    frontZ,
    nestWidth,
    nestDepth,
    nestCenterZ,
    trayDepth: frontZ - backZ,
    trayCenterZ: (backZ + frontZ) * 0.5,
    portDepth,
    bayZ,
  };
}

/**
 * Layout of front load ports for ProcessEquipment — mirrors mesh placement.
 */
export function getProcessEquipmentPortPoses(
  width: number,
  height: number,
  depth: number,
  loadPortCount: number,
  /**
   * Along-dock fractions from the 2D ports (`rx` on a bottom/top face).
   * When omitted the bank is centred with {@link fitProcessLoadPortBank} pitch.
   */
  portFractions?: readonly number[]
): EquipmentPortPose[] {
  const ports = clampProcessLoadPortCount(loadPortCount);
  const metrics = resolveProcessLoadPortMetrics(width, height, depth, ports);
  const portDepth = metrics.depth;
  const portW = metrics.width;
  const bank = fitProcessLoadPortBank(
    ports,
    width,
    metrics.width,
    metrics.pitch
  );
  const pitch = bank.pitch;

  // The door is sized around the FOUP that has to pass through it rather than
  // around the tool, so the panel can retract fully into the port tower pocket
  // instead of overshooting the roof, and the opening stays a believable
  // doorway on both compact and large-footprint tools.
  const foupSize = KIT_FOUP_BASE_WIDTH * resolveKitFoupScale(width, height, depth);
  const shutterW = Math.min(portW * 0.9, Math.max(foupSize * 1.35, portW * 0.5));
  const shutterH = Math.min(height * 0.34, Math.max(foupSize * 1.5, height * 0.16));
  const shutterD = Math.max(0.04, portDepth * 0.06);
  const deck = getProcessEquipmentDeck(width, height, depth, ports, portFractions);
  /** FOUP base rests here — also the chamber deck height. */
  const deckY = deck.deckY;

  return Array.from({ length: ports }, (_, i) => {
    const x = resolveLoadPortSeatX(
      portFractions?.[i],
      width,
      deck.nestWidth,
      (i - (ports - 1) / 2) * pitch
    );
    const bayZ = deck.bayZ;
    const loadPort: Vec3 = [x, deckY, deck.foupSeatZ];
    // Straight above the handoff line: the hoist drops over the nest front edge.
    const ohtHandoff: Vec3 = [x, height * EQUIPMENT_OHT_HANDOFF_HEIGHT_RATIO, deck.seatZ];
    const internal: Vec3 = [x, deckY, bayZ - portDepth * 0.22];
    // Panel exactly covers the doorway when closed.
    const shutter: Vec3 = [x, deckY + shutterH * 0.5, bayZ + portDepth * 0.08];
    return {
      bay: [x, 0, bayZ],
      ohtHandoff,
      loadPort,
      internal,
      shutter,
      shutterSize: [shutterW, shutterH, shutterD],
      // Slightly over one panel height so the doorway is fully cleared.
      shutterOpenTravel: shutterH * 1.04,
    };
  });
}

/**
 * Single-bay LoadPort kit poses.
 *
 * Shares {@link getProcessEquipmentDeck} with the process tool so a standalone
 * bay seats its FOUP on the same plane, scales its nest with the footprint, and
 * honours the same along-face fraction — otherwise the tray sat at the kit
 * centre while the streamed drop node sat at the port's own `rx`.
 */
export function getStandaloneLoadPortPoses(
  width: number,
  height: number,
  depth: number,
  portFractions?: readonly number[]
): EquipmentPortPose[] {
  const deck = getProcessEquipmentDeck(width, height, depth, 1, portFractions);
  const x = resolveLoadPortSeatX(portFractions?.[0], width, deck.nestWidth, 0);
  const trayY = deck.deckY;
  const shutterH = height * 0.45;
  const shutterW = Math.min(width * 0.55, deck.nestWidth);
  return [
    {
      bay: [x, 0, deck.bayZ],
      ohtHandoff: [x, trayY, deck.seatZ],
      loadPort: [x, trayY, deck.foupSeatZ],
      internal: [x, trayY, depth * 0.05],
      shutter: [x, trayY + shutterH * 0.45, depth * 0.18],
      shutterSize: [shutterW, shutterH, 0.045],
      shutterOpenTravel: shutterH * 0.9,
    },
  ];
}

function shutterFromProgress(open01: number): { shutterOpen: number; shutterState: ShutterState } {
  const shutterOpen = clamp01(open01);
  if (shutterOpen <= 0.001) return { shutterOpen: 0, shutterState: 'closed' };
  if (shutterOpen >= 0.999) return { shutterOpen: 1, shutterState: 'open' };
  return { shutterOpen, shutterState: shutterOpen > 0.5 ? 'opening' : 'closing' };
}

function segmentProgress(t: number, a: number, b: number): number {
  if (b <= a) return t >= b ? 1 : 0;
  return clamp01((t - a) / (b - a));
}

/** Staged fab-lifter waypoints — chamber → down → traverse → OHT node → load port. */
export type FabLifterWaypoints = {
  chamber: Vec3;
  /** After vertical descent from chamber (same X as chamber). */
  lifterLow: Vec3;
  /** Horizontal track point aligned with the active port X. */
  trackPoint: Vec3;
  /** OHT rail handoff — same X/Y/Z reference the hoist uses. */
  ohtNode: Vec3;
  loadPort: Vec3;
};

/** Segment weights for fab-lifter motion (down · horizontal · node · deck). */
export const FAB_LIFTER_SEGMENT_WEIGHTS = [0.22, 0.28, 0.25, 0.25] as const;

export function resolveFabLifterWaypoints(pose: EquipmentPortPose): FabLifterWaypoints {
  const chamber = pose.internal;
  const loadPort = pose.loadPort;
  const ohtNode = pose.ohtHandoff;
  const lifterRailY = loadPort[1] + (chamber[1] - loadPort[1]) * 0.14;
  const lifterTrackZ = loadPort[2] * 0.52 + ohtNode[2] * 0.48;
  return {
    chamber,
    lifterLow: [chamber[0], lifterRailY, lifterTrackZ],
    trackPoint: [loadPort[0], lifterRailY, lifterTrackZ],
    ohtNode,
    loadPort,
  };
}

/** Multi-segment eased path through ordered waypoints. */
export function sampleMultiSegmentPath(
  points: readonly Vec3[],
  uRaw: number,
  segmentWeights: readonly number[] = FAB_LIFTER_SEGMENT_WEIGHTS
): Vec3 {
  if (points.length === 0) return [0, 0, 0];
  if (points.length === 1) return points[0]!;
  const weights = segmentWeights.slice(0, points.length - 1);
  const total = weights.reduce((sum, w) => sum + w, 0) || 1;
  let t = clamp01(uRaw) * total;
  for (let i = 0; i < weights.length; i += 1) {
    const w = weights[i]!;
    if (t <= w || i === weights.length - 1) {
      return lerp3(points[i]!, points[i + 1]!, t / w);
    }
    t -= w;
  }
  return points[points.length - 1]!;
}

export type FabLifterPathDirection = 'toLoadPort' | 'toChamber';

/** Chamber ↔ load port via vertical descent, horizontal traverse, OHT node, deck seat. */
export function sampleFabLifterPath(
  waypoints: FabLifterWaypoints,
  u: number,
  direction: FabLifterPathDirection
): Vec3 {
  const chain: Vec3[] =
    direction === 'toLoadPort'
      ? [
          waypoints.chamber,
          waypoints.lifterLow,
          waypoints.trackPoint,
          waypoints.ohtNode,
          waypoints.loadPort,
        ]
      : [
          waypoints.loadPort,
          waypoints.ohtNode,
          waypoints.trackPoint,
          waypoints.lifterLow,
          waypoints.chamber,
        ];
  return sampleMultiSegmentPath(chain, u);
}

/** Fab process tool: load deck → upper chamber lifter (no lower-bay shutter cycle). */
export type EquipmentTransferPathStyle = 'default' | 'fab-lifter';

export type ResolveEquipmentTransferArgs = {
  isPickup: boolean;
  progress: number;
  portIndex: number;
  poses: EquipmentPortPose[];
  /** Equipment-local rail node. Defaults to the legacy vertical handoff pose. */
  handoffPose?: Vec3;
  /** Stable FOUP id for ownership (optional). */
  foupId?: string;
  pathStyle?: EquipmentTransferPathStyle;
};

/**
 * Resolve per-port visuals for the active transfer. Inactive ports stay closed/idle.
 */
export function resolveEquipmentTransferVisuals(
  args: ResolveEquipmentTransferArgs
): EquipmentPortVisual[] {
  const { isPickup, progress, portIndex, poses, foupId, handoffPose, pathStyle = 'default' } =
    args;
  const p = clamp01(progress);
  const F = EQUIPMENT_TRANSFER_FRACS;

  return poses.map((pose, i) => {
    if (i !== portIndex) {
      return {
        portIndex: i,
        shutterOpen: 0,
        shutterState: 'closed' as const,
        transferState: 'idle' as const,
        occupancy: 'empty' as const,
        foupMode: 'hidden' as const,
        foupPosition: null,
        isLocked: false,
      };
    }

    if (isPickup) {
      return pathStyle === 'fab-lifter'
        ? resolveFabLifterPickupPort(p, pose, handoffPose ?? pose.ohtHandoff, i, foupId)
        : resolvePickupPort(p, pose, handoffPose ?? pose.ohtHandoff, i, foupId);
    }
    return pathStyle === 'fab-lifter'
      ? resolveFabLifterDropPort(p, pose, handoffPose ?? pose.ohtHandoff, i, foupId)
      : resolveDropPort(p, pose, handoffPose ?? pose.ohtHandoff, i, foupId);
  });

  function resolveDropPort(
    progress: number,
    pose: EquipmentPortPose,
    node: Vec3,
    idx: number,
    id?: string
  ): EquipmentPortVisual {
    const base = {
      portIndex: idx,
      isLocked: true,
      foupId: id,
    };

    if (progress < F.alignEnd) {
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'reserved',
        occupancy: 'reserved',
        foupMode: 'hidden',
        foupPosition: null,
      };
    }

    if (progress < F.dropOhtRelease) {
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'reserved',
        occupancy: 'reserved',
        foupMode: 'hidden',
        foupPosition: null,
      };
    }

    if (progress < F.dropShutterOpenEnd) {
      const open = segmentProgress(progress, F.dropOhtRelease, F.dropShutterOpenEnd);
      const { shutterOpen, shutterState } = shutterFromProgress(open);
      return {
        ...base,
        shutterOpen,
        shutterState: shutterState === 'closed' || shutterState === 'open' ? shutterState : 'opening',
        transferState: 'opening_shutter',
        occupancy: 'loading',
        foupMode: 'resting',
        foupPosition: node,
      };
    }

    if (progress < F.dropFoupInside) {
      const u = segmentProgress(progress, F.dropShutterOpenEnd, F.dropFoupInside);
      return {
        ...base,
        shutterOpen: 1,
        shutterState: 'open',
        transferState: 'loading',
        occupancy: 'loading',
        foupMode: 'flying',
        // Stay at the OHT release point until the shutter is open, then slide inboard.
        foupPosition: sampleFoupPath(node, node, pose.internal, u, false),
      };
    }

    if (progress < F.dropShutterClosed) {
      // FOUP inside — close shutter after ownership commit.
      const closeU = segmentProgress(progress, F.dropFoupInside, F.dropShutterClosed);
      const open = 1 - closeU;
      return {
        ...base,
        shutterOpen: open,
        shutterState: open <= 0.001 ? 'closed' : 'closing',
        transferState: open <= 0.001 ? 'idle' : 'closing_shutter',
        occupancy: 'occupied',
        foupMode: 'resting',
        foupPosition: pose.internal,
        isLocked: open > 0.001,
      };
    }

    return {
      ...base,
      shutterOpen: 0,
      shutterState: 'closed',
      transferState: 'idle',
      occupancy: 'occupied',
      foupMode: 'resting',
      foupPosition: pose.internal,
      isLocked: false,
    };
  }

  /** DROP: OHT → load deck → upper chamber. Shutter stays hidden after FOUP lands. */
  function resolveFabLifterDropPort(
    progress: number,
    pose: EquipmentPortPose,
    _node: Vec3,
    idx: number,
    id?: string
  ): EquipmentPortVisual {
    void _node;
    const base = { portIndex: idx, isLocked: true, foupId: id };

    if (progress < F.alignEnd) {
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'reserved',
        occupancy: 'reserved',
        foupMode: 'hidden',
        foupPosition: null,
      };
    }

    if (progress < F.dropOhtRelease) {
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'reserved',
        occupancy: 'reserved',
        foupMode: 'hidden',
        foupPosition: null,
      };
    }

    if (progress < F.dropShutterOpenEnd) {
      // OHT releases onto load deck — hold until lifter picks up.
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'loading',
        occupancy: 'loading',
        foupMode: 'resting',
        foupPosition: pose.loadPort,
      };
    }

    if (progress < F.dropFoupInside) {
      const u = segmentProgress(progress, F.dropShutterOpenEnd, F.dropFoupInside);
      const waypoints = resolveFabLifterWaypoints(pose);
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'loading',
        occupancy: 'loading',
        foupMode: 'flying',
        foupPosition: sampleFabLifterPath(waypoints, u, 'toChamber'),
      };
    }

    if (progress < F.dropShutterClosed) {
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'idle',
        occupancy: 'occupied',
        foupMode: 'resting',
        foupPosition: pose.internal,
        isLocked: false,
      };
    }

    return {
      ...base,
      shutterOpen: 0,
      shutterState: 'closed',
      transferState: 'idle',
      occupancy: 'occupied',
      foupMode: 'resting',
      foupPosition: pose.internal,
      isLocked: false,
    };
  }

  /** PICKUP: upper chamber → load deck → OHT. */
  function resolveFabLifterPickupPort(
    progress: number,
    pose: EquipmentPortPose,
    _node: Vec3,
    idx: number,
    id?: string
  ): EquipmentPortVisual {
    void _node;
    const base = { portIndex: idx, isLocked: true, foupId: id };

    if (progress < F.alignEnd) {
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'reserved',
        occupancy: 'occupied',
        foupMode: 'resting',
        foupPosition: pose.internal,
      };
    }

    if (progress < F.pickupShutterOpenEnd) {
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'unloading',
        occupancy: 'occupied',
        foupMode: 'resting',
        foupPosition: pose.internal,
      };
    }

    const liftToLoadPortEnd = 0.52;

    if (progress < liftToLoadPortEnd) {
      const u = segmentProgress(progress, F.pickupShutterOpenEnd, liftToLoadPortEnd);
      const waypoints = resolveFabLifterWaypoints(pose);
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'unloading',
        occupancy: 'unloading',
        foupMode: 'flying',
        foupPosition: sampleFabLifterPath(waypoints, u, 'toLoadPort'),
      };
    }

    if (progress < F.pickupOhtTake) {
      // Lifter delivered FOUP — hold on load deck for OHT legs.
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'shutter_open',
        occupancy: 'unloading',
        foupMode: 'resting',
        foupPosition: pose.loadPort,
      };
    }

    if (progress < F.pickupShutterClosed) {
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'idle',
        occupancy: 'empty',
        foupMode: 'hidden',
        foupPosition: null,
        isLocked: false,
        foupId: undefined,
      };
    }

    return {
      ...base,
      shutterOpen: 0,
      shutterState: 'closed',
      transferState: 'idle',
      occupancy: 'empty',
      foupMode: 'hidden',
      foupPosition: null,
      isLocked: false,
      foupId: undefined,
    };
  }

  function resolvePickupPort(
    progress: number,
    pose: EquipmentPortPose,
    node: Vec3,
    idx: number,
    id?: string
  ): EquipmentPortVisual {
    const base = {
      portIndex: idx,
      isLocked: true,
      foupId: id,
    };

    if (progress < F.alignEnd) {
      return {
        ...base,
        shutterOpen: 0,
        shutterState: 'closed',
        transferState: 'reserved',
        occupancy: 'occupied',
        foupMode: 'resting',
        foupPosition: pose.internal,
      };
    }

    if (progress < F.pickupShutterOpenEnd) {
      const open = segmentProgress(progress, F.alignEnd, F.pickupShutterOpenEnd);
      return {
        ...base,
        shutterOpen: open,
        shutterState: open >= 0.999 ? 'open' : 'opening',
        transferState: 'opening_shutter',
        occupancy: 'occupied',
        foupMode: 'resting',
        foupPosition: pose.internal,
      };
    }

    if (progress < F.pickupFoupAtNode) {
      const u = segmentProgress(progress, F.pickupShutterOpenEnd, F.pickupFoupAtNode);
      return {
        ...base,
        shutterOpen: 1,
        shutterState: 'open',
        transferState: 'unloading',
        occupancy: 'unloading',
        foupMode: 'flying',
        foupPosition: sampleFoupPath(node, pose.loadPort, pose.internal, u, true),
      };
    }

    if (progress < F.pickupOhtTake) {
      return {
        ...base,
        shutterOpen: 1,
        shutterState: 'open',
        transferState: 'shutter_open',
        occupancy: 'unloading',
        foupMode: 'resting',
        foupPosition: node,
      };
    }

    // OHT owns FOUP — hide equipment FOUP, close shutter.
    if (progress < F.pickupShutterClosed) {
      const closeU = segmentProgress(progress, F.pickupOhtTake, F.pickupShutterClosed);
      const open = 1 - closeU;
      return {
        ...base,
        shutterOpen: open,
        shutterState: open <= 0.001 ? 'closed' : 'closing',
        transferState: open <= 0.001 ? 'idle' : 'closing_shutter',
        occupancy: 'empty',
        foupMode: 'hidden',
        foupPosition: null,
        isLocked: open > 0.001,
        foupId: undefined,
      };
    }

    return {
      ...base,
      shutterOpen: 0,
      shutterState: 'closed',
      transferState: 'idle',
      occupancy: 'empty',
      foupMode: 'hidden',
      foupPosition: null,
      isLocked: false,
      foupId: undefined,
    };
  }
}

/**
 * Which load-port index handles this transfer.
 * DROP → 0, PICKUP → 1 when ≥2 ports; otherwise 0.
 */
export function resolveActiveEquipmentPortIndex(
  isPickup: boolean,
  loadPortCount: number,
  preferredIndex?: number | null
): number {
  const n = Math.max(1, Math.min(6, Math.round(loadPortCount)));
  if (typeof preferredIndex === 'number' && preferredIndex >= 0 && preferredIndex < n) {
    return preferredIndex;
  }
  if (n === 1) return 0;
  return isPickup ? Math.min(1, n - 1) : 0;
}

/** Validation gates before starting a drop (pure check). */
export function canStartEquipmentDrop(opts: {
  ohtHasFoup: boolean;
  ohtAligned: boolean;
  portEmpty: boolean;
  portLocked: boolean;
  equipmentAvailable: boolean;
}): boolean {
  return (
    opts.ohtHasFoup &&
    opts.ohtAligned &&
    opts.portEmpty &&
    !opts.portLocked &&
    opts.equipmentAvailable
  );
}

/** Validation gates before starting a pickup. */
export function canStartEquipmentPickup(opts: {
  ohtEmpty: boolean;
  ohtAligned: boolean;
  portHasFoup: boolean;
  portLocked: boolean;
  equipmentAvailable: boolean;
}): boolean {
  return (
    opts.ohtEmpty &&
    opts.ohtAligned &&
    opts.portHasFoup &&
    !opts.portLocked &&
    opts.equipmentAvailable
  );
}

/**
 * Equipment process light from FOUP location:
 * - Red RUNNING only while sticky red window is active (caller gates 10s)
 * - FOUP out for pickup → OFFLINE (gray)
 * - Otherwise IDLE (green)
 */
export function resolveEquipmentProcessStatus(
  portVisuals: EquipmentPortVisual[],
  stickyProcessing: boolean,
  redLedActive = false
): EquipmentStatus {
  const active = portVisuals.find(
    (v) => v.isLocked || v.occupancy !== 'empty' || v.foupMode !== 'hidden'
  );

  if (active?.occupancy === 'unloading' || active?.transferState === 'unloading') {
    return 'OFFLINE';
  }

  if (redLedActive) return 'RUNNING';

  if (
    stickyProcessing ||
    active?.occupancy === 'occupied' ||
    active?.occupancy === 'loading' ||
    active?.transferState === 'loading'
  ) {
    // FOUP inside but red window finished → green idle docked.
    return 'IDLE';
  }

  return 'IDLE';
}

/** Wall-clock red LED duration after shutter closes with FOUP inside. */
export const PROCESS_RED_LED_MS = 10_000;
